import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';
import { CompanySessionService } from './company-session.service';
import { PartnerSessionService } from '@core/partner-auth/partner-session.service';
import { AdminSessionService } from '@core/admin-auth/admin-session.service';
import { AUTH_TOKEN_TYPE } from './auth-token.context';
import { NotificationService } from '@core/notification/notification.service';
import { LOCAL_STORAGE } from '@core/storage/storage.tokens';
import { LOCAL_KEYS } from '@core/storage/storage.keys';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const storage = inject(LOCAL_STORAGE);
  const companySession = inject(CompanySessionService);
  const partnerSession = inject(PartnerSessionService);
  const adminSession = inject(AdminSessionService);
  const notification = inject(NotificationService);
  const translate = inject(TranslateService);

  const requiresToken = req.context.has(AUTH_TOKEN_TYPE);
  const tokenType = req.context.get(AUTH_TOKEN_TYPE);

  let token: string | null = null;
  if (tokenType === 'company') {
    token = storage.get(LOCAL_KEYS.companyToken) ?? companySession.companyToken();
  } else if (tokenType === 'employer') {
    token = auth.employerToken();
  } else if (tokenType === 'partner') {
    token = partnerSession.token();
  } else if (tokenType === 'admin') {
    token = adminSession.token();
  }
  // tokenType === 'none' → token stays null

  if (requiresToken && tokenType !== 'none' && !token) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 0,
          statusText: 'Missing Auth Token',
          url: req.url,
        }),
    );
  }

  const outgoingReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(outgoingReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Partner Portal requests end only the partner session — never the employer one.
      if (tokenType === 'partner') {
        // INVALID_CREDENTIALS is a wrong current password (Change password), not an expired session.
        if (
          error.status === 401 &&
          error.error?.error_code !== 'INVALID_CREDENTIALS' &&
          partnerSession.isAuthenticated()
        ) {
          notification.error(translate.instant('auth.errors.session_expired'));
          partnerSession.logout('expired');
        } else if (error.status === 403 && error.error?.error_code === 'PARTNER_SUSPENDED') {
          partnerSession.logout('suspended');
        }
        return throwError(() => error);
      }

      // Backoffice requests end only the admin session.
      if (tokenType === 'admin') {
        // INVALID_CREDENTIALS is a wrong current password (Change password), not an expired session.
        // Several requests can fail at once: only the first ends the session and says so.
        if (
          error.status === 401 &&
          error.error?.error_code !== 'INVALID_CREDENTIALS' &&
          adminSession.isAuthenticated()
        ) {
          notification.error(translate.instant('auth.errors.session_expired'));
          adminSession.logout();
        }
        return throwError(() => error);
      }

      // INVALID_CREDENTIALS is a wrong confirmation password (e.g. deleting the account),
      // not an expired session — the caller reports it and the session stays.
      if (error.status === 401 && tokenType !== 'none' && error.error?.error_code !== 'INVALID_CREDENTIALS') {
        notification.error(translate.instant('auth.errors.session_expired'));
        auth.logout();
      }

      if (error.status === 403 && error.error?.error_code === 'COMPANY_ACCESS_DENIED') {
        companySession.clearSession();
        notification.error(translate.instant('auth.errors.company_access_changed'));
        auth.logout();
      }

      return throwError(() => error);
    }),
  );
};
